import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight, Shield, Clock, FileText,
  Users, Zap, X, Star, Award,
  Lock, Globe, CheckCircle, Quote,
  Scale, FileCheck, XCircle, TrendingUp,
  AlertTriangle, ChevronRight
} from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { JarvisEngine } from '../components/JarvisEngine';
import { WizardProvider } from '../context/WizardContext';
import { useLanguage } from '../context/LanguageContext';
import { Wizard } from '../components/wizard/Wizard';
import { Dialog, DialogContent } from '../components/ui/dialog';

const testimonials = [
  {
    name: 'Maria Silva',
    city: 'Sao Paulo',
    destination: 'Portugal',
    photo: 'https://images.unsplash.com/photo-1562012962-5da0c554dac3?w=200&h=200&fit=crop&crop=face',
    status: 'APROVADO',
    score: 78,
    comment: 'Consegui meu visto de trabalho para Portugal em 2 meses! O relatorio me mostrou exatamente o que precisava. Economizei semanas de pesquisa e R$1.500 em consultas.'
  },
  {
    name: 'Joao Pedro Santos',
    city: 'Rio de Janeiro',
    destination: 'Estados Unidos',
    photo: 'https://images.unsplash.com/photo-1694185752676-07b7336747f2?w=200&h=200&fit=crop&crop=face',
    status: 'APROVADO',
    score: 82,
    comment: 'Estava perdido com a documentacao para os EUA. O LEGISLAI me deu um checklist completo e personalizado pro meu caso. Passei na entrevista de primeira!'
  },
  {
    name: 'Ana Oliveira',
    city: 'Curitiba',
    destination: 'Canada',
    photo: 'https://images.unsplash.com/photo-1613275139344-0018ad151c09?w=200&h=200&fit=crop&crop=face',
    status: 'APROVADO',
    score: 72,
    comment: 'Meu score era 72 e o relatorio premium me mostrou exatamente onde melhorar. Em 3 meses consegui a aprovacao. Vale cada centavo!'
  }
];

const stats = [
  { value: '2.847+', label: 'Casos Analisados' },
  { value: '94%', label: 'Taxa de Sucesso' },
  { value: '<3min', label: 'Tempo de Analise' },
  { value: '4.9/5', label: 'Avaliacao' },
];

const topCountries = [
  { name: 'EUA', flag: 'us' },
  { name: 'Portugal', flag: 'pt' },
  { name: 'Espanha', flag: 'es' },
  { name: 'Canada', flag: 'ca' },
  { name: 'Italia', flag: 'it' },
  { name: 'Alemanha', flag: 'de' },
  { name: 'Australia', flag: 'au' },
  { name: 'Japao', flag: 'jp' },
  { name: 'Reino Unido', flag: 'gb' },
  { name: 'Franca', flag: 'fr' },
];

export default function LandingPage() {
  const [showWizard, setShowWizard] = useState(false);
  const { language, setLanguage } = useLanguage();

  const handlePreviewComplete = (result) => {
    setShowWizard(false);
    window.location.href = `/preview/${result.case_id}`;
  };

  return (
    <div className="min-h-screen bg-[#050608] overflow-x-hidden">
      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative min-h-screen flex flex-col">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(0,30,60,0.35) 0%, rgba(5,6,8,1) 65%)' }} />

        {/* Header */}
        <header className="relative z-20 px-6 py-4">
          <nav className="max-w-7xl mx-auto flex items-center justify-between" data-testid="nav-header">
            <img src="/assets/logo-legislai.png" alt="FORTUNATO LEGISLAI" className="h-14 md:h-16 w-auto" data-testid="logo-img" />
            <div className="flex items-center gap-3">
              <LanguageSelector currentLanguage={language} onLanguageChange={setLanguage} />
              <button data-testid="header-cta" onClick={() => setShowWizard(true)}
                className="hidden md:flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-semibold text-sm uppercase tracking-wider hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all">
                Gerar Meu Plano <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </nav>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center px-6 py-8">
          <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left: Copy */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="text-left">
              {/* Countries badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex -space-x-1">
                  {topCountries.slice(0, 5).map((c, i) => (
                    <img key={i} src={`https://flagcdn.com/w40/${c.flag}.png`} alt={c.name} className="w-6 h-auto rounded shadow border border-black/40" />
                  ))}
                </div>
                <span className="text-gray-500 text-xs uppercase tracking-wider">10 paises disponiveis</span>
              </div>

              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-[3.4rem] text-white leading-[1.15] mb-6">
                Descubra agora se voce{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C9A24A] to-[#E0B75B]">
                  pode morar na Europa ou Estados Unidos
                </span>
              </h1>

              <p className="text-gray-300 text-base lg:text-lg mb-3 max-w-lg leading-relaxed">
                Resultado em menos de 3 minutos.
              </p>
              <p className="text-gray-500 text-sm mb-8 max-w-lg">
                Sem advogados. Sem promessas falsas. Analise estrategica baseada em fontes oficiais.
              </p>

              {/* CTA */}
              <button data-testid="hero-cta" onClick={() => setShowWizard(true)}
                className="px-10 py-4 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-bold uppercase tracking-wider text-sm flex items-center gap-3 hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all shadow-lg shadow-[#C9A24A]/20 hover:shadow-[#C9A24A]/40 hover:scale-[1.02] active:scale-[0.98] mb-4">
                Gerar Meu Plano de Imigracao
                <TrendingUp className="w-5 h-5" />
              </button>
              <p className="text-gray-500 text-xs mb-8 max-w-md">
                Analise preliminar gratuita + Plano de Acao Personalizado (PDF) por preco promocional.
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats.map((stat, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 + i * 0.1 }}>
                    <div className="text-[#C9A24A] font-bold text-xl lg:text-2xl">{stat.value}</div>
                    <div className="text-gray-500 text-xs mt-0.5">{stat.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-5 mt-6 text-xs">
                <div className="flex items-center gap-2 text-gray-500"><Lock className="w-3 h-3 text-[#C9A24A]" /> Dados Criptografados</div>
                <div className="flex items-center gap-2 text-gray-500"><Shield className="w-3 h-3 text-[#C9A24A]" /> 100% Confidencial</div>
                <div className="flex items-center gap-2 text-gray-500"><Globe className="w-3 h-3 text-[#C9A24A]" /> Fontes Oficiais</div>
              </div>
            </motion.div>

            {/* Right: Engine */}
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.3 }}
              className="flex flex-col items-center justify-center">
              <div className="text-center mb-3">
                <span className="text-[#00D4FF] text-xs uppercase tracking-[0.3em] font-medium">Motor de Score de Visto</span>
              </div>
              <JarvisEngine size={380} />
              <div className="text-center mt-3">
                <span className="text-gray-600 text-xs uppercase tracking-[0.2em]">Conectado as Fontes Oficiais</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ RISCO / URGENCIA ═══════════════ */}
      <section className="relative z-10 py-16 px-6 bg-[#080a0e] border-t border-red-500/10" data-testid="risk-section">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center gap-3 mb-8 justify-center">
              <AlertTriangle className="w-6 h-6 text-red-400" />
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white">
                Voce pode estar iniciando pelo caminho errado
              </h2>
            </div>

            <div className="space-y-4 max-w-xl mx-auto">
              {[
                { text: '72% das pessoas iniciam o processo migratório errado', highlight: true },
                { text: 'Muitos gastam milhares antes de entender sua elegibilidade' },
                { text: 'Consultas tradicionais custam R$500 a R$2.000 — e nem sempre resolvem' },
                { text: 'Um erro de estrategia pode atrasar sua regularizacao por anos' },
              ].map((item, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }} viewport={{ once: true }}
                  className={`flex items-start gap-3 p-4 border-l-2 ${item.highlight ? 'border-red-400 bg-red-500/5' : 'border-red-400/30 bg-red-500/[0.02]'}`}
                >
                  <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${item.highlight ? 'text-red-400' : 'text-red-400/60'}`} />
                  <p className={`text-sm ${item.highlight ? 'text-red-300 font-medium' : 'text-gray-400'}`}>{item.text}</p>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-10">
              <button data-testid="risk-cta" onClick={() => setShowWizard(true)}
                className="px-10 py-4 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-3 hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all">
                Gerar Meu Plano de Imigracao <ChevronRight className="w-5 h-5" />
              </button>
              <p className="text-gray-600 text-xs mt-3">Score gratuito. Plano de acao por preco promocional.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════ COMO FUNCIONA (SIMPLES) ═══════════════ */}
      <section className="relative z-10 py-20 px-6 bg-[#050608]" data-testid="how-it-works">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#C9A24A] text-xs uppercase tracking-widest">Como Funciona</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mt-4">
              3 passos. Sem termos tecnicos.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { num: '01', title: 'Responda perguntas estrategicas', desc: 'Sobre seu perfil, destino e objetivo. Leva menos de 3 minutos.', icon: FileText, color: '#C9A24A' },
              { num: '02', title: 'O sistema cruza com legislacao oficial', desc: 'IA analisa seu caso com fontes oficiais atualizadas de cada pais.', icon: Scale, color: '#00D4FF' },
              { num: '03', title: 'Receba seu score + rota recomendada', desc: 'Pontuacao de 0 a 100, rotas legais e checklist de documentos.', icon: TrendingUp, color: '#C9A24A' },
            ].map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }} viewport={{ once: true }} className="text-center" data-testid={`step-${step.num}`}>
                <div className="relative inline-block mb-5">
                  <div className="w-20 h-20 rounded-full border flex items-center justify-center" style={{ borderColor: `${step.color}40`, background: `${step.color}10` }}>
                    <step.icon className="w-8 h-8" style={{ color: step.color }} />
                  </div>
                  <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-[#050608] font-bold text-sm" style={{ background: step.color }}>
                    {step.num}
                  </span>
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ FONTES OFICIAIS (CREDIBILIDADE) ═══════════════ */}
      <section className="relative z-10 py-16 px-6 bg-[#080a0e] border-t border-b border-white/5" data-testid="sources-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-white text-lg font-semibold tracking-wide">
              Baseado em Fontes Oficiais Atualizadas
            </h2>
            <p className="text-gray-500 text-sm mt-2">Legislacao verificada de cada pais de destino</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { flag: 'es', name: 'Espanha', source: 'Ministerio de Inclusion' },
              { flag: 'pt', name: 'Portugal', source: 'AIMA' },
              { flag: 'us', name: 'EUA', source: 'USCIS' },
              { flag: 'ca', name: 'Canada', source: 'IRCC' },
              { flag: 'au', name: 'Australia', source: 'Home Affairs' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2 p-4 bg-black/30 border border-white/5" data-testid={`source-${item.flag}`}>
                <img src={`https://flagcdn.com/w80/${item.flag}.png`} alt={item.name} className="w-10 h-auto rounded shadow" />
                <span className="text-white text-sm font-medium">{item.name}</span>
                <span className="text-gray-500 text-xs text-center">{item.source}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
            {[
              { flag: 'it', name: 'Italia', source: 'Min. dell\'Interno' },
              { flag: 'de', name: 'Alemanha', source: 'BAMF' },
              { flag: 'jp', name: 'Japao', source: 'ISA Japan' },
              { flag: 'gb', name: 'Reino Unido', source: 'UKVI' },
              { flag: 'fr', name: 'Franca', source: 'France-Visas' },
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2 p-4 bg-black/30 border border-white/5" data-testid={`source-${item.flag}`}>
                <img src={`https://flagcdn.com/w80/${item.flag}.png`} alt={item.name} className="w-10 h-auto rounded shadow" />
                <span className="text-white text-sm font-medium">{item.name}</span>
                <span className="text-gray-500 text-xs text-center">{item.source}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ COMPARACAO ═══════════════ */}
      <section className="relative z-10 py-16 px-6 bg-[#050608]" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[#C9A24A] text-sm uppercase tracking-widest">Comparacao</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mt-4">
              Consulta Tradicional vs. LEGISLAI
            </h2>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto text-sm">
              Um despachante cobra entre R$500 a R$2.000 por consulta e voce espera semanas. Com o LEGISLAI, voce recebe uma analise <strong className="text-gray-200">personalizada para o seu caso</strong> em minutos.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="bg-black/40 border border-red-500/20 p-6" data-testid="comparison-lawyer">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-white font-semibold text-lg">Despachante / Advogado</h3>
                <span className="text-red-400 text-2xl font-['Playfair_Display']">R$500-2.000</span>
              </div>
              <ul className="space-y-3 text-sm">
                {['Necessita agendamento presencial', 'Espera de dias ou semanas por resposta', 'Consulta generica de 30-60 min', 'Sem checklist personalizado para seu caso', 'Sem score de aprovacao', 'Cada duvida nova = nova consulta paga'].map((t, i) => (
                  <li key={i} className="flex items-center gap-3"><XCircle className="w-4 h-4 text-red-400/70 flex-shrink-0" /><span className="text-gray-500">{t}</span></li>
                ))}
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              className="bg-black/40 border border-[#C9A24A]/30 p-6 relative overflow-hidden" data-testid="comparison-legislai">
              <div className="absolute top-0 right-0 px-3 py-1 bg-[#C9A24A] text-[#050608] text-xs font-bold uppercase">Melhor Valor</div>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-white font-semibold text-lg">FORTUNATO LEGISLAI</h3>
                <span className="text-[#C9A24A] text-2xl font-['Playfair_Display']">R$49,90</span>
              </div>
              <div className="text-xs text-[#C9A24A]/70 mt-1 text-center">Oferta de Lancamento no Brasil</div>
              <ul className="space-y-3 text-sm">
                {['100% online, resultado em minutos', 'Analise detalhada do SEU caso especifico', 'Score de aprovacao personalizado com IA', 'Checklist de documentos sob medida', 'Passo a passo juridico para evitar erros e negativas', 'Ideal para quem quer imigrar com planejamento e economia'].map((t, i) => (
                  <li key={i} className="flex items-center gap-3"><CheckCircle className="w-4 h-4 text-[#C9A24A] flex-shrink-0" /><span className="text-gray-300">{t}</span></li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ DEPOIMENTOS ═══════════════ */}
      <section className="relative z-10 py-20 px-6 bg-gradient-to-b from-[#050608] to-[#080a0e]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A24A] text-sm uppercase tracking-widest">Casos Reais de Brasileiros</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mt-4">Quem Confiou, Conseguiu</h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto text-sm">
              Cada caso era unico. Cada relatorio foi personalizado. Veja como ajudamos brasileiros a realizar o sonho.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }} viewport={{ once: true }}
                className="bg-black/40 border border-[#C9A24A]/10 p-6 relative hover:border-[#C9A24A]/25 transition-colors" data-testid={`testimonial-card-${i}`}>
                <Quote className="absolute top-4 right-4 w-8 h-8 text-[#C9A24A]/20" />
                <div className="flex items-center gap-4 mb-4">
                  <img src={t.photo} alt={t.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#C9A24A]/30" data-testid={`testimonial-photo-${i}`} />
                  <div>
                    <h4 className="text-white font-semibold">{t.name}</h4>
                    <p className="text-gray-500 text-sm">{t.city}, Brasil</p>
                    <p className="text-[#00D4FF] text-xs">Destino: {t.destination}</p>
                  </div>
                </div>
                <p className="text-gray-400 text-sm italic mb-4 leading-relaxed">"{t.comment}"</p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">{[...Array(5)].map((_, j) => (<Star key={j} className="w-3.5 h-3.5 fill-[#C9A24A] text-[#C9A24A]" />))}</div>
                    <span className="text-gray-600 text-xs">Score: {t.score}/100</span>
                  </div>
                  <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-full">{t.status}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ PAISES ═══════════════ */}
      <section className="relative z-10 py-16 px-6 bg-[#080a0e]" data-testid="countries-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white">
              Analise para os 10 Paises Mais Procurados
            </h2>
            <p className="text-gray-400 mt-3 text-sm">Selecione seu destino e descubra sua pontuacao de aprovacao</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 max-w-3xl mx-auto">
            {topCountries.map((c, i) => (
              <motion.button key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }} viewport={{ once: true }}
                onClick={() => setShowWizard(true)}
                className="flex flex-col items-center gap-2 p-4 bg-black/40 border border-white/5 hover:border-[#C9A24A]/40 transition-all cursor-pointer group" data-testid={`country-${c.flag}`}>
                <img src={`https://flagcdn.com/w80/${c.flag}.png`} alt={c.name} className="w-12 h-auto rounded shadow group-hover:scale-110 transition-transform" />
                <span className="text-gray-400 text-xs font-medium group-hover:text-[#C9A24A] transition-colors">{c.name}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ PRICING ═══════════════ */}
      <section className="relative z-10 py-20 px-6 bg-[#050608]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white mb-3">Escolha Seu Plano</h2>
            <p className="text-gray-400 max-w-xl mx-auto text-sm">Score de visto gratuito. Relatorios completos a precos acessiveis.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Free */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-black/40 border border-white/10 p-6 text-center" data-testid="pricing-free">
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">Score Gratis</h3>
              <p className="text-gray-500 text-sm mb-4">Descubra suas chances</p>
              <div className="py-3 mb-4 border-y border-white/5"><span className="text-3xl font-['Playfair_Display'] text-[#00D4FF]">R$0</span></div>
              <ul className="text-sm text-gray-400 space-y-2 mb-6 text-left">
                {['Score de aprovacao (0-100)', 'Avaliacao preliminar com IA', 'Resultado imediato'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#00D4FF] flex-shrink-0" />{t}</li>
                ))}
              </ul>
              <button onClick={() => setShowWizard(true)} data-testid="pricing-free-cta"
                className="w-full py-3 border border-[#00D4FF]/30 text-[#00D4FF] font-semibold uppercase tracking-wider text-sm hover:bg-[#00D4FF]/10 transition-colors">
                Gerar Meu Score
              </button>
            </motion.div>

            {/* Basic R$49.99 */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-black/40 border border-[#00D4FF]/30 p-6 text-center" data-testid="pricing-basic">
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">Relatorio Essencial</h3>
              <p className="text-gray-500 text-sm mb-4">Visto + Checklist Completo</p>
              <div className="py-3 mb-4 border-y border-[#00D4FF]/10">
                <span className="text-3xl font-['Playfair_Display'] text-white">R$49,90</span>
                <span className="text-[#00D4FF] text-xs block mt-1">ou 3x de R$16,63</span>
              </div>
              <ul className="text-sm text-gray-400 space-y-2 mb-6 text-left">
                {['Tudo do Score Gratis', 'Rotas legais detalhadas', 'Checklist de documentos', 'Cronograma 7/14/30 dias', 'PDF profissional'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#00D4FF] flex-shrink-0" />{t}</li>
                ))}
              </ul>
              <button onClick={() => setShowWizard(true)} data-testid="pricing-basic-cta"
                className="w-full py-3 bg-[#00D4FF] text-[#000510] font-semibold uppercase tracking-wider text-sm hover:bg-white transition-colors">
                Gerar Meu Diagnostico
              </button>
            </motion.div>

            {/* Premium R$149,90 */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-black/40 border-2 border-[#C9A24A]/50 p-6 text-center relative overflow-hidden" data-testid="pricing-premium">
              <div className="absolute top-0 right-0 px-3 py-1 bg-[#C9A24A] text-[#050608] text-xs font-bold uppercase">Popular</div>
              <h3 className="font-['Playfair_Display'] text-xl text-white mb-2">Plano VIP</h3>
              <p className="text-gray-500 text-sm mb-4">Relatorio + Suporte WhatsApp</p>
              <div className="py-3 mb-4 border-y border-[#C9A24A]/20">
                <span className="text-3xl font-['Playfair_Display'] text-transparent bg-clip-text bg-gradient-to-r from-[#C9A24A] to-[#E0B75B]">R$149,90</span>
                <span className="text-[#C9A24A] text-xs block mt-1">ou 3x de R$49,97</span>
              </div>
              <ul className="text-sm text-gray-400 space-y-2 mb-6 text-left">
                {['Tudo do Essencial', 'Dicas para entrevista consular', 'Checklist detalhado passo a passo', 'Estrategia de aprovacao', 'Suporte direto via WhatsApp com especialista'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#C9A24A] flex-shrink-0" />{t}</li>
                ))}
              </ul>
              <button onClick={() => setShowWizard(true)} data-testid="pricing-premium-cta"
                className="w-full py-3 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-bold uppercase tracking-wider text-sm hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all">
                Quero o Plano VIP
              </button>
            </motion.div>
          </div>

          {/* Guarantee */}
          <div className="flex justify-center mt-10">
            <div className="inline-flex items-center gap-3 bg-green-500/10 border border-green-500/20 px-8 py-4 rounded-lg" data-testid="guarantee-badge">
              <Shield className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-green-300 text-sm font-semibold">Garantia Incondicional de 7 Dias</p>
                <p className="text-green-400/70 text-xs">Se nao ficar satisfeito, devolvemos 100% do valor. Sem perguntas.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ DIOGO FORTUNATO ═══════════════ */}
      <section className="relative z-10 py-16 px-6 bg-[#080a0e] border-t border-[#C9A24A]/10">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-8 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="flex justify-center md:justify-start">
              <img src="/assets/diogo-fortunato.png" alt="Dr. Diogo Fortunato"
                className="w-44 h-44 rounded-full object-cover border-2 border-[#C9A24A]/40 shadow-lg shadow-[#C9A24A]/10" data-testid="diogo-photo" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-[#C9A24A] text-xs uppercase tracking-widest font-semibold">Criado por quem entende</span>
              <h3 className="font-['Playfair_Display'] text-2xl text-white mt-2 mb-3">Dr. Diogo Fortunato</h3>
              <p className="text-[#C9A24A] text-sm font-medium mb-4">Advogado em Espanha | Especialista em Imigracao Internacional</p>
              <p className="text-gray-400 text-sm leading-relaxed mb-4">
                Criador da tecnologia que ja ajudou mais de 2.800 brasileiros a planejar seu processo migratorio para mais de 10 paises. O Dr. Diogo Fortunato desenvolveu o LEGISLAI para democratizar o acesso a informacao de qualidade sobre imigracao, eliminando as barreiras de custo e distancia.
              </p>
              <p className="text-gray-500 text-sm leading-relaxed italic">
                "Cada caso de imigracao e unico. Nao existe formula magica. Por isso criei o LEGISLAI: para analisar <strong className="text-gray-300 not-italic">o seu caso especifico</strong>, com as leis atualizadas do seu pais de destino, e te dar um caminho claro."
              </p>
              <div className="flex flex-wrap gap-4 mt-6 text-xs text-gray-500">
                <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#C9A24A]" /> 10+ paises de atuacao</div>
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#C9A24A]" /> 2.800+ casos analisados</div>
                <div className="flex items-center gap-2"><Award className="w-4 h-4 text-[#C9A24A]" /> 94% taxa de sucesso</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA FINAL ═══════════════ */}
      <section className="relative z-10 py-16 px-6 bg-[#050608] border-t border-[#C9A24A]/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white mb-4">
            Seu sonho de morar fora comeca com informacao certa
          </h2>
          <p className="text-gray-400 mb-8 text-sm">
            Descubra em minutos o que levaria semanas pesquisando sozinho. Score gratuito, sem compromisso.
          </p>
          <button data-testid="final-cta" onClick={() => setShowWizard(true)}
            className="px-12 py-4 bg-gradient-to-r from-[#C9A24A] to-[#E0B75B] text-[#050608] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-3 hover:from-[#E0B75B] hover:to-[#C9A24A] transition-all shadow-lg shadow-[#C9A24A]/20">
            Gerar Meu Plano de Imigracao <TrendingUp className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="relative z-10 py-12 px-6 bg-[#050608] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-4 mb-8 pb-8 border-b border-white/5">
            <img src="/assets/diogo-fortunato.png" alt="Dr. Diogo Fortunato" className="w-12 h-12 rounded-full object-cover border border-[#C9A24A]/30" />
            <div className="text-center md:text-left">
              <p className="text-white text-sm font-medium">Dr. Diogo Fortunato</p>
              <p className="text-[#C9A24A] text-xs">Especialista em Imigracao Internacional</p>
            </div>
            <p className="md:ml-auto text-gray-600 text-xs">Ajudando brasileiros a realizarem o sonho de morar no exterior</p>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src="/assets/logo-legislai.png" alt="FORTUNATO LEGISLAI" className="h-12 w-auto opacity-70" />
            <p className="text-gray-600 text-sm text-center max-w-lg" data-testid="footer-disclaimer">
              O FORTUNATO LEGISLAI utiliza inteligencia artificial para analisar seu caso especifico com fontes oficiais de imigracao. Cada relatorio e unico. Este servico nao substitui aconselhamento juridico formal.
            </p>
            <p className="text-gray-700 text-xs">&copy; {new Date().getFullYear()} FORTUNATO LEGISLAI</p>
          </div>
        </div>
      </footer>

      {/* ═══════════════ WIZARD MODAL ═══════════════ */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0a0c10] border-[#C9A24A]/20">
          <div className="absolute top-4 right-4">
            <button data-testid="close-wizard-btn" onClick={() => setShowWizard(false)} className="text-gray-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="py-6 px-2">
            <WizardProvider>
              <Wizard onPreviewComplete={handlePreviewComplete} />
            </WizardProvider>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
